/**
 * Contract duration helpers.
 *
 * Contracts persist only `start_date` / `end_date` (DATE strings). The duration
 * "number of months" is a UI convenience: the end date is derived as
 * start + N months - 1 day (e.g. a 12-month term starting Jan 15 2026 ends
 * Jan 14 2027). Dates are parsed/formatted in local time to avoid timezone drift.
 */

/** Parse a 'YYYY-MM-DD' string into a local Date, or null when invalid. */
function parseLocalDate(dateStr: string): Date | null {
  const parts = (dateStr || '').split('-').map((p) => parseInt(p, 10))
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null
  const [y, m, d] = parts
  const date = new Date(y, m - 1, d)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Format a Date as a local 'YYYY-MM-DD' string. */
function formatLocalDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Compute end_date = start_date + months - 1 day.
 * Returns '' when the start date or month count is invalid.
 */
export function computeEndDateFromMonths(
  startDateStr: string,
  months: number
): string {
  const start = parseLocalDate(startDateStr)
  if (!start || !Number.isFinite(months) || months <= 0) return ''
  const end = new Date(start)
  end.setMonth(end.getMonth() + Math.floor(months))
  end.setDate(end.getDate() - 1)
  return formatLocalDate(end)
}

/**
 * Best-effort inverse of computeEndDateFromMonths: if the given end date is
 * exactly start + N months - 1 day for some 1..600 month span, return N so the
 * UI can pre-select "months" mode; otherwise return null.
 */
export function inferDurationMonths(
  startDateStr: string,
  endDateStr: string
): number | null {
  if (!parseLocalDate(startDateStr) || !parseLocalDate(endDateStr)) return null
  for (let n = 1; n <= 600; n++) {
    if (computeEndDateFromMonths(startDateStr, n) === endDateStr) return n
  }
  return null
}
